import React from 'react';
import { getProjectColor, getProjectInitials } from '../utils/projectColors';

const BACKEND_URL = window.location.origin;

/**
 * Reusable project avatar — colored box with the first 1–2 letters of the
 * project's name. Color is taken from `project.color` (backend-assigned)
 * with a deterministic fallback for legacy projects.
 *
 * If `project.logo_url` exists, the image is shown instead of initials,
 * but the color border/background is preserved as a frame.
 *
 * Sizes:
 *   xs  → 28px (w-7 h-7)
 *   sm  → 32px (w-8 h-8)
 *   md  → 40px (w-10 h-10)
 *   lg  → 56px (w-14 h-14)
 *   xl  → 64px (w-16 h-16)
 */
const SIZE_STYLES = {
  xs: 'w-7 h-7 text-[10px] rounded-md',
  sm: 'w-8 h-8 text-xs rounded-lg',
  md: 'w-10 h-10 text-sm rounded-lg',
  lg: 'w-14 h-14 text-base rounded-xl',
  xl: 'w-16 h-16 text-lg rounded-xl',
};

function resolveLogoUrl(rawUrl) {
  if (!rawUrl) return null;
  if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;
  return `${BACKEND_URL}${rawUrl}`;
}

export default function ProjectIcon({ project, size = 'lg', className = '', style = {}, withRing = false }) {
  const color = getProjectColor(project);
  const initials = getProjectInitials(project?.name);
  const sizeCls = SIZE_STYLES[size] || SIZE_STYLES.lg;
  const logoSrc = resolveLogoUrl(project?.logo_url);
  const [imgError, setImgError] = React.useState(false);

  // Reset error flag when src changes
  React.useEffect(() => { setImgError(false); }, [logoSrc]);

  const showImage = logoSrc && !imgError;

  return (
    <div
      data-testid={`project-icon-${project?.id || 'unknown'}`}
      className={`flex items-center justify-center font-bold flex-shrink-0 select-none shadow-sm overflow-hidden ${sizeCls} ${className}`}
      style={{
        backgroundColor: color.bg,
        color: color.text,
        boxShadow: withRing ? `inset 0 0 0 1.5px ${color.ring}` : undefined,
        ...style,
      }}
      title={project?.name}
    >
      {showImage ? (
        <img
          src={logoSrc}
          alt={project?.name || 'project logo'}
          className="w-full h-full object-contain"
          onError={() => setImgError(true)}
          draggable={false}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}
